import type { Breadcrumb } from "@sentry/react";

const SENSITIVE_PATTERNS = [
  "token",
  "password",
  "secret",
  "auth",
  "card",
  "cvv",
  "curp",
  "rfc",
  "clabe",
  "credit",
  "account_number",
  "session",
  "jwt",
  "bearer",
  "nss",
  "ine",
] as const;

const PRESCRUBBED_BREADCRUMB_DATA_KEYS = {
  "vault.email-lookup": ["domain", "status"],
  "vault.form": ["requirementId", "failedFields"],
  "vault.navigation": [
    "activeRequirementId",
    "registeredRequirementId",
    "registrantRequirementId",
  ],
} as const;

type PrescrubbedBreadcrumbCategory =
  keyof typeof PRESCRUBBED_BREADCRUMB_DATA_KEYS;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isPrescrubbedBreadcrumbCategory = (
  category: string,
): category is PrescrubbedBreadcrumbCategory =>
  Object.prototype.hasOwnProperty.call(
    PRESCRUBBED_BREADCRUMB_DATA_KEYS,
    category,
  );

export const containsSensitiveText = (value: string): boolean => {
  const normalized = value.toLowerCase();
  return SENSITIVE_PATTERNS.some((pattern) => normalized.includes(pattern));
};

const sanitizeVaultBreadcrumbData = (
  category: PrescrubbedBreadcrumbCategory,
  data: Breadcrumb["data"],
) => {
  const source: Record<string, unknown> = isRecord(data) ? data : {};

  if (category === "vault.form") {
    const failedFields = Array.isArray(source.failedFields)
      ? source.failedFields
          .filter(isRecord)
          .map((field) => ({
            key: typeof field.key === "string" ? field.key : "",
            message: typeof field.message === "string" ? field.message : "",
          }))
          .filter((field) => field.key && field.message)
      : undefined;

    return {
      ...(typeof source.requirementId === "string"
        ? { requirementId: source.requirementId }
        : {}),
      ...(failedFields ? { failedFields } : {}),
    };
  }

  if (category === "vault.email-lookup") {
    return {
      ...(typeof source.domain === "string" ? { domain: source.domain } : {}),
      ...(typeof source.status === "number" ? { status: source.status } : {}),
    };
  }

  return Object.fromEntries(
    PRESCRUBBED_BREADCRUMB_DATA_KEYS[category]
      .filter((key) => {
        const value = source[key];
        return (
          typeof value === "string" ||
          typeof value === "number" ||
          typeof value === "boolean" ||
          value === null
        );
      })
      .map((key) => [key, source[key]]),
  );
};

export const scrubBreadcrumbs = (
  breadcrumbs: readonly Breadcrumb[],
): Breadcrumb[] =>
  breadcrumbs.map((breadcrumb) => {
    const category = breadcrumb.category;
    if (category && isPrescrubbedBreadcrumbCategory(category)) {
      return {
        ...breadcrumb,
        data: sanitizeVaultBreadcrumbData(category, breadcrumb.data),
      };
    }

    const messageContainsSensitive = SENSITIVE_PATTERNS.some(
      (p) =>
        breadcrumb.message?.toLowerCase().includes(p) ||
        JSON.stringify(breadcrumb.data || {})
          .toLowerCase()
          .includes(p),
    );

    if (messageContainsSensitive) {
      return {
        ...breadcrumb,
        message: "[Filtered]",
        data: {},
      };
    }

    return breadcrumb;
  });
